import { CallAPI } from "../../apiManager/ApiCall";
import { getAddSlidePresentationByTitleIdURL } from "../../apiManager/APIConfig";

/** In-memory cache so opening a category doesn't re-fetch every mount. */
const previewCache = new Map();

function asUrl(value) {
  if (!value) return null;
  if (typeof value === "string") return value.trim() || null;
  if (typeof value === "object") {
    return asUrl(value.url || value.thumbnailUrl || value.src || value.content);
  }
  return null;
}

/** Deep-zoom tile template → static preview tile used elsewhere in the app. */
export function tileUrlToThumbnail(url) {
  const raw = asUrl(url);
  if (!raw) return null;
  if (raw.includes("{z}")) {
    return raw
      .replace("{z}/{x}/{y}.png", "3/3/3.png")
      .replace("{z}/{x}/{y}.jpg", "3/3/3.jpg")
      .replace("{z}/{x}/{y}.jpeg", "3/3/3.jpeg")
      .replace("{z}/{x}/{y}.webp", "3/3/3.webp")
      .replace("{z}/{x}/{y}", "3/3/3");
  }
  return raw;
}

/** True when the slide has anything worth showing in the mini preview. */
export function slideHasPreviewContent(slide) {
  const data = Array.isArray(slide?.content?.data) ? slide.content.data : [];
  return data.some((el) => {
    if (!el) return false;
    if (el.type === "TEXT") {
      const html = typeof el.content === "string" ? el.content : "";
      return Boolean(html.replace(/<[^>]+>/g, "").trim());
    }
    if (el.type === "IMAGE" || el.type === "VIDEO" || el.type === "SLIDE") {
      return Boolean(asUrl(el.content) || (el.type === "SLIDE" && el.content));
    }
    return false;
  });
}

export function getSlideElements(slide) {
  if (!slide) return [];
  return Array.isArray(slide?.content?.data) ? slide.content.data : [];
}

/**
 * Fetch presentation slides without touching Redux (list page must not
 * overwrite the maker's slide store).
 */
export async function fetchPresentationSlidesSilent(titleId) {
  if (!titleId || titleId === "undefined") return [];
  try {
    const response = await CallAPI({
      url: `${getAddSlidePresentationByTitleIdURL}/${titleId}`,
      method: "GET",
    });
    const slides = response?.data?.data ?? response?.data ?? response;
    return Array.isArray(slides) ? slides : [];
  } catch (_) {
    return [];
  }
}

/**
 * Returns { slideCount, tiles[4] } for a presentation subtitle id.
 * Each tile is { elements, empty } for the mini preview renderer.
 */
export async function getPresentationPreview(titleId) {
  const emptyTiles = () =>
    [0, 1, 2, 3].map(() => ({ elements: [], empty: true }));

  if (!titleId) {
    return { slideCount: 0, tiles: emptyTiles() };
  }
  if (previewCache.has(titleId)) {
    return previewCache.get(titleId);
  }

  const pending = (async () => {
    const slides = await fetchPresentationSlidesSilent(titleId);
    const tiles = [0, 1, 2, 3].map((i) => {
      const slide = slides[i] || null;
      if (!slide || !slideHasPreviewContent(slide)) {
        return { elements: [], empty: true };
      }
      return { elements: getSlideElements(slide), empty: false };
    });
    return {
      slideCount: slides.length,
      tiles,
    };
  })();

  previewCache.set(titleId, pending);
  try {
    const result = await pending;
    previewCache.set(titleId, result);
    return result;
  } catch (_) {
    previewCache.delete(titleId);
    return { slideCount: 0, tiles: emptyTiles() };
  }
}

export function invalidatePresentationPreview(titleId) {
  if (titleId) previewCache.delete(titleId);
}

export function clearPresentationPreviewCache() {
  previewCache.clear();
}

export function formatPresentationTimestamp(dateStr) {
  if (!dateStr) return "—";
  const date = new Date(dateStr);
  if (Number.isNaN(date.getTime())) return "—";
  const day = date.getDate();
  const month = date.toLocaleString(undefined, { month: "short" });
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${day} ${month} ${year}, ${hours}:${minutes}`;
}
